import { AnyAction } from '@reduxjs/toolkit'

import type { FetchStatusCommon } from 'restApi/types'
import type { BookingData } from './bookingReducer'

type ChangeRequestData = {
  id: number
  start_date: string
  end_date: string
  total_guests: number
  adults: number
  children: number
  pets?: number
  status: string
  original_price: string
  new_price: string
  price_difference: string
  changed: any[]
  can_accept: boolean
  calculated_refund: {
    voucher_refund_amount: string
    total_refund: string
  }
  customer_service_fee: string
  tax_amount: string
  special_offer_price: string
  by_host: boolean
  currency: string
  deferred_payment_eligible: boolean
  due_now_amount: string
  due_on_amount: string
  due_on_date: string
  total_paid: string
  line_items: {
    item_type: string
    price: string
    quantity: number
    amount: string
    details: {
      date: string
      price: number
    }[]
  }[]
  tax_rate_details: {
    name: string
    rate: string
    juris_type: string
    included: boolean
  }[]
  // The change request serializes the listing's live pricing via the same
  // ListingSerializer the booking uses, so reuse that shape rather than
  // maintaining a second copy that can drift.
  listing: BookingData['listing']
}

type ChangeRequestState = FetchStatusCommon & {
  data: ChangeRequestData
  error: Record<string, string>
}

const initialState: ChangeRequestState = {
  fetching: false,
  fetched: false,
  updating: false,
  updated: false,
  submitting: false,
  error: null,
  data: null,
}

export default (
  state = initialState,
  action: AnyAction
): ChangeRequestState => {
  switch (action.type) {
    case 'BOOKING_CHANGE_REQUEST_FETCH_PENDING':
      return {
        ...state,
        fetching: true,
      }

    case 'BOOKING_CHANGE_REQUEST_FETCH_REJECTED':
      return {
        ...state,
        fetching: false,
        error: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_FETCH_FULFILLED':
      return {
        ...state,
        fetching: false,
        data: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_SUBMIT_PENDING':
      return {
        ...state,
        updating: true,
        submitting: true,
      }

    case 'BOOKING_CHANGE_REQUEST_SUBMIT_REJECTED':
      return {
        ...state,
        updating: false,
        submitting: false,
        error: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_SUBMIT_FULFILLED':
      return {
        ...state,
        updating: false,
        submitting: false,
        data: null,
      }

    case 'BOOKING_CHANGE_REQUEST_ACCEPT_PENDING':
      return {
        ...state,
        updating: true,
        updated: false,
      }

    case 'BOOKING_CHANGE_REQUEST_ACCEPT_REJECTED':
      return {
        ...state,
        updating: false,
        error: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_ACCEPT_FULFILLED': {
      return {
        ...state,
        updating: false,
        updated: true,
        data: action.payload.data,
      }
    }

    case 'BOOKING_CHANGE_REQUEST_DECLINE_PENDING':
      return {
        ...state,
        updating: true,
        updated: false,
      }

    case 'BOOKING_CHANGE_REQUEST_DECLINE_REJECTED':
      return {
        ...state,
        updating: false,
        error: action.payload.data,
      }

    case 'BOOKING_CHANGE_REQUEST_DECLINE_FULFILLED': {
      return {
        ...state,
        updating: false,
        updated: true,
        data: action.payload.data,
      }
    }

    default:
      return state
  }
}
